import React, { useMemo, useState, useEffect } from "react";
import { ChevronUp, ChevronDown } from "../../../../components/icons/svg/outline";
import { calculateLineItemTotal } from "./TMTable/helpers";

/**
 * SummaryTable Component
 * Displays summary calculations matching the backend PDF generation
 * Shows subtotals, markups, taxes, and totals with editable percentages
 * 
 * @param {Array} lineItems - Array of line items for calculation
 * @param {Object} percentages - Object containing editable percentages
 * @param {Function} onPercentagesChange - Callback when percentages change
 */
const SummaryTable = ({ lineItems = [], percentages = {}, onPercentagesChange }) => {
  // Calculate category subtotals from line items (matching backend logic)
  const categorySubtotals = useMemo(() => {
    const LABOR_TYPES = ['labor', 'custom_labor'];
    const EQUIPMENT_TYPES = ['equipment', 'custom_equipment'];
    const SERVICE_TYPES = ['subcontractors'];
    // Materials include: material, concrete, custom_material, dumping

    let laborSubtotal = 0;
    let equipmentSubtotal = 0;
    let materialSubtotalBeforeTax = 0;
    let servicesSubtotal = 0;

    lineItems.forEach((item) => {
      const extended = calculateLineItemTotal(item);

      if (LABOR_TYPES.includes(item.type)) {
        laborSubtotal += extended;
      } else if (EQUIPMENT_TYPES.includes(item.type)) {
        equipmentSubtotal += extended;
      } else if (SERVICE_TYPES.includes(item.type)) {
        servicesSubtotal += extended;
      } else {
        // Materials (material, concrete, custom_material, dumping)
        materialSubtotalBeforeTax += extended;
      }
    });

    return {
      labor: parseFloat(laborSubtotal.toFixed(2)),
      equipment: parseFloat(equipmentSubtotal.toFixed(2)),
      materialBeforeTax: parseFloat(materialSubtotalBeforeTax.toFixed(2)),
      services: parseFloat(servicesSubtotal.toFixed(2)),
    };
  }, [lineItems]);

  // Helper function to parse percentage with default, allowing 0 values
  const parsePercentage = (value, defaultValue) => {
    if (value == null) return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Calculate all summary values (matching backend save() method)
  const summary = useMemo(() => {
    const {
      labor: laborSubtotal,
      equipment: equipmentSubtotal,
      materialBeforeTax: materialSubtotalBeforeTax,
      services: servicesSubtotal,
    } = categorySubtotals;

    // Get percentages (defaults match backend model defaults)
    // Use helper function to allow 0 values (|| would treat 0 as falsy)
    const laborMarkupPct = parsePercentage(percentages.labor_markup_percentage, 0.35);
    const equipmentMarkupPct = parsePercentage(percentages.equipment_markup_percentage, 0.15);
    const materialMarkupPct = parsePercentage(percentages.material_markup_percentage, 0.15);
    const materialTaxPct = parsePercentage(percentages.material_tax_percentage, 0.0875);
    const servicesMarkupPct = parsePercentage(percentages.services_markup_percentage, 0.10);
    const biPct = parsePercentage(percentages.bi_percentage, 0.035);

    // Step 1: Calculate material tax
    const materialTax = parseFloat((materialSubtotalBeforeTax * materialTaxPct).toFixed(2));
    const materialSubtotalWithTax = parseFloat((materialSubtotalBeforeTax + materialTax).toFixed(2));

    // Step 2: Calculate markup amounts
    const laborMarkup = parseFloat((laborSubtotal * laborMarkupPct).toFixed(2));
    const equipmentMarkup = parseFloat((equipmentSubtotal * equipmentMarkupPct).toFixed(2));
    const materialMarkup = parseFloat((materialSubtotalWithTax * materialMarkupPct).toFixed(2));
    const servicesMarkup = parseFloat((servicesSubtotal * servicesMarkupPct).toFixed(2));

    // Step 3: Calculate totals with markup
    const laborTotal = parseFloat((laborSubtotal + laborMarkup).toFixed(2));
    const equipmentTotal = parseFloat((equipmentSubtotal + equipmentMarkup).toFixed(2));
    const materialTotal = parseFloat((materialSubtotalWithTax + materialMarkup).toFixed(2));
    const servicesTotal = parseFloat((servicesSubtotal + servicesMarkup).toFixed(2));

    // Step 4: Calculate subtotal after all markups
    const subtotalAfterMarkup = parseFloat((laborTotal + equipmentTotal + materialTotal + servicesTotal).toFixed(2));

    // Step 5: Calculate B&I
    const biAmount = parseFloat((subtotalAfterMarkup * biPct).toFixed(2));

    // Step 6: Calculate grand total
    const grandTotal = parseFloat((subtotalAfterMarkup + biAmount).toFixed(2));

    return {
      labor: {
        subtotal: laborSubtotal,
        markupPct: laborMarkupPct,
        markup: laborMarkup,
        total: laborTotal,
      },
      equipment: {
        subtotal: equipmentSubtotal,
        markupPct: equipmentMarkupPct,
        markup: equipmentMarkup,
        total: equipmentTotal,
      },
      material: {
        subtotalBeforeTax: materialSubtotalBeforeTax,
        taxPct: materialTaxPct,
        tax: materialTax,
        subtotalWithTax: materialSubtotalWithTax,
        markupPct: materialMarkupPct,
        markup: materialMarkup,
        total: materialTotal,
      },
      services: {
        subtotal: servicesSubtotal,
        markupPct: servicesMarkupPct,
        markup: servicesMarkup,
        total: servicesTotal,
      },
      subtotalAfterMarkup,
      bi: {
        percentage: biPct,
        amount: biAmount,
      },
      grandTotal,
    };
  }, [categorySubtotals, percentages]);

  // Handle percentage change - converts from percentage (e.g., 35) to decimal (e.g., 0.35)
  const handlePercentageChange = (field, value) => {
    // If value is a string with % sign, remove it
    const cleanValue = typeof value === 'string' ? value.replace('%', '') : value;
    const numValue = parseFloat(cleanValue) || 0;
    // Convert percentage to decimal (35 -> 0.35)
    const decimalValue = numValue / 100;
    // Clamp between 0 and 1, then round to 4 decimal places (matching backend DecimalField decimal_places=4)
    const clampedValue = Math.max(0, Math.min(1, decimalValue));
    const roundedValue = parseFloat(clampedValue.toFixed(4));
    onPercentagesChange?.({
      ...percentages,
      [field]: roundedValue,
    });
  };

  // Convert decimal to percentage for display (0.35 -> 35)
  const decimalToPercentage = (decimalValue) => {
    if (decimalValue === null || decimalValue === undefined) return "";
    return (parseFloat(decimalValue) * 100).toFixed(2);
  };

  // Format currency
  const formatCurrency = (value) => {
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Reusable percentage input with arrows - displays as percentage (35) but stores as decimal (0.35)
  const PercentageInput = ({ value, fieldName }) => {
    const displayValue = decimalToPercentage(value);
    const [localValue, setLocalValue] = useState(displayValue);
    const [isFocused, setIsFocused] = useState(false);

    // Update local value when prop changes, but only if input is not focused
    useEffect(() => {
      if (!isFocused) {
        setLocalValue(displayValue);
      }
    }, [displayValue, isFocused]);

    const handleInputChange = (e) => {
      setLocalValue(e.target.value);
    };

    const handleInputBlur = () => {
      setIsFocused(false);
      // Sync to parent on blur
      handlePercentageChange(fieldName, localValue);
    };

    const handleInputFocus = () => {
      setIsFocused(true);
    };

    const handleInputKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.target.blur(); // This will trigger handleInputBlur
      }
    };

    const handleIncrement = () => {
      const currentDecimal = parseFloat(value) || 0;
      const currentPercent = currentDecimal * 100;
      const newPercent = Math.min(100, parseFloat((currentPercent + 0.1).toFixed(2)));
      handlePercentageChange(fieldName, newPercent);
    };

    const handleDecrement = () => {
      const currentDecimal = parseFloat(value) || 0;
      const currentPercent = currentDecimal * 100;
      const newPercent = Math.max(0, parseFloat((currentPercent - 0.1).toFixed(2)));
      handlePercentageChange(fieldName, newPercent);
    };

    return (
      <div className="flex items-center justify-center">
        <input
          type="number"
          inputMode="decimal"
          value={localValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          className="caret-black w-14 h-[24px] pl-1 pr-1 py-0 border border-gray-300 rounded-l-md rounded-r-none focus:outline-none focus:ring-1 focus:ring-blue-500 text-right text-xs"
          min="0"
          max="100"
          step="any"
        />
        <span className="px-1 text-xs text-gray-600 border-y border-r py-1 border-gray-300">%</span>
        <div className="flex flex-col border border-l-0 border-gray-300 rounded-r-md shrink-0">
          <button
            type="button"
            onClick={handleIncrement}
            className="h-[12px] w-[16px] flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 border-b border-gray-300"
            tabIndex={-1}
          >
            <ChevronUp className="w-2 h-2 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={handleDecrement}
            className="h-[12px] w-[16px] flex items-center justify-center hover:bg-gray-100 active:bg-gray-200"
            tabIndex={-1}
          >
            <ChevronDown className="w-2 h-2 text-gray-600" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="border border-gray-300 rounded-md bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full min-[840px]:min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="pl-2 pr-1 py-5 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Category
              </th>
              <th className="px-[2px] md:px-2 text-center md:text-right text-xs font-medium uppercase tracking-wider text-gray-700">
                Subtotal
              </th>
              <th className="px-[2px] md:px-2 text-center text-xs font-medium uppercase tracking-wider text-gray-700">
                Tax %
              </th>
              <th className="px-[2px] md:px-2 text-center text-xs font-medium uppercase tracking-wider text-gray-700">
                Markup %
              </th>
              <th className="px-[2px] md:px-2 text-center md:text-right text-xs font-medium uppercase tracking-wider text-gray-700">
                Total (W/Markup)
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Labor Row */}
            <tr className="bg-blue-50 hover:bg-blue-100">
              <td className="pl-2 pr-1 text-sm text-gray-900">Labor</td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700">
                ${formatCurrency(summary.labor.subtotal)}
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center text-gray-500">-</td>
              <td className="px-[2px] md:px-2 text-sm text-center">
                <PercentageInput
                  value={percentages.labor_markup_percentage}
                  fieldName="labor_markup_percentage"
                />
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-900">
                ${formatCurrency(summary.labor.total)}
              </td>
            </tr>

            {/* Equipment Row */}
            <tr className="bg-yellow-50 hover:bg-yellow-100">
              <td className="pl-2 pr-1 text-sm text-gray-900">Equipment</td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700">
                ${formatCurrency(summary.equipment.subtotal)}
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center text-gray-500">-</td>
              <td className="px-[2px] md:px-2 text-sm text-center">
                <PercentageInput
                  value={percentages.equipment_markup_percentage}
                  fieldName="equipment_markup_percentage"
                />
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-900">
                ${formatCurrency(summary.equipment.total)}
              </td>
            </tr>

            {/* Material Row */}
            <tr className="bg-green-50 hover:bg-green-100">
              <td className="pl-2 pr-1 text-sm text-gray-900">Material</td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700">
                ${formatCurrency(summary.material.subtotalBeforeTax)}
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center">
                <PercentageInput
                  value={percentages.material_tax_percentage}
                  fieldName="material_tax_percentage"
                />
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center">
                <PercentageInput
                  value={percentages.material_markup_percentage}
                  fieldName="material_markup_percentage"
                />
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-900">
                ${formatCurrency(summary.material.total)}
              </td>
            </tr>

            {/* Services/Subcontractor Row */}
            <tr className="bg-purple-50 hover:bg-purple-100">
              <td className="pl-2 pr-1 text-sm text-gray-900">Serv/Sub</td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700">
                ${formatCurrency(summary.services.subtotal)}
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center text-gray-500">-</td>
              <td className="px-[2px] md:px-2 text-sm text-center">
                <PercentageInput
                  value={percentages.services_markup_percentage}
                  fieldName="services_markup_percentage"
                />
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-900">
                ${formatCurrency(summary.services.total)}
              </td>
            </tr>

            {/* Subtotal Row */}
            <tr className="bg-gray-50 border-t-2 border-gray-300">
              <td className="pl-2 pr-1 text-sm text-gray-900 font-semibold">Subtotal</td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700" colSpan="3"></td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-900 font-semibold">
                ${formatCurrency(summary.subtotalAfterMarkup)}
              </td>
            </tr>

            {/* B&I Row */}
            <tr className="hover:bg-gray-50">
              <td className="pl-2 pr-1 text-sm text-gray-900">B & I</td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700" colSpan="2"></td>
              <td className="px-[2px] md:px-2 text-sm text-center">
                <div className="flex justify-center items-center">
                  <PercentageInput
                    value={percentages.bi_percentage}
                    fieldName="bi_percentage"
                  />
                </div>
              </td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-900">
                ${formatCurrency(summary.bi.amount)}
              </td>
            </tr>

            {/* Grand Total Row */}
            <tr className="bg-gray-100 border-t-2 border-gray-400">
              <td className="pl-2 pr-1 py-2 text-sm text-gray-900 font-semibold">Total for this report</td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700" colSpan="3"></td>
              <td className="px-[2px] md:px-2 text-sm text-center md:text-right font-bold text-base text-gray-900">
                ${formatCurrency(summary.grandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SummaryTable;
